import logger from "@airhelp/node-logger";
import type { LanguageCode } from "config/languages";
import { useServerSideStoryblokPages } from "contexts/DataContext/hooks";
import getDomainFromLanguage from "utils/getDomainFromLanguage";
import { getLocaleFromUrl } from "utils/getLocale";
import { getSlugWithoutLocale } from "utils/getSlugWithoutLocale";
import { isDefaultLanguage } from "utils/isDefaultLanguage";
import { getParentLanguage } from "utils/localeUtils";
import { storyblokPageUuids, type StoryblokPageName } from "config/storyblokPageUuids";

const resolveDefaultUrl = (url: string, language: LanguageCode, domain: string) => {
  const storySlug = getSlugWithoutLocale(url);
  return `${domain}/${language}/${storySlug}/`;
};

const getStoryblokPageUrl = (language: LanguageCode, pageName: StoryblokPageName): string => {
  const pageId = storyblokPageUuids[pageName];
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const pages = useServerSideStoryblokPages();
  const domain = getDomainFromLanguage(language);

  const page = pages[pageId];
  const parentLanguage = getParentLanguage(language);
  const parentLanguageDomain = getDomainFromLanguage(parentLanguage);

  const { [language]: languageUrl, [parentLanguage]: parentLanguageUrl, en: englishUrl } = page;
  const englishLanguageDomain = getDomainFromLanguage("en");

  if (languageUrl) {
    const isDefault = isDefaultLanguage(getLocaleFromUrl(languageUrl));

    return isDefault ? resolveDefaultUrl(languageUrl, language, domain) : `${domain}${languageUrl}/`;
  }

  if (parentLanguageUrl) {
    const isDefault = isDefaultLanguage(getLocaleFromUrl(parentLanguageUrl));

    return isDefault
      ? resolveDefaultUrl(parentLanguageUrl, language, parentLanguageDomain)
      : `${parentLanguageDomain}${parentLanguageUrl}/`;
  }

  logger.warn(`Url for ${pageName} in "${language}" was not found.`);

  if (englishUrl) {
    const isDefault = isDefaultLanguage(getLocaleFromUrl(englishUrl));

    return isDefault
      ? resolveDefaultUrl(englishUrl, "en", englishLanguageDomain)
      : `${englishLanguageDomain}${englishUrl}/`;
  }

  logger.error(`Url for ${pageName} in was not found.`);

  return "/";
};

export default getStoryblokPageUrl;
